// src/components/common/ReversalConfirmDialog/ReversalConfirmDialog.jsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { getOrderStatusReasons } from '../../../services/api/orderReference';
import styles from './ReversalConfirmDialog.module.css';

/**
 * Confirmation dialog requiring user to type "YES" before reversing.
 * Offers two outcomes for the reversed order(s): back to Confirmed, or to Cancelled
 * (the latter requires a reason, reusing the same requiresReason pattern as OrderStatusSection).
 *
 * Props:
 *  isOpen        boolean
 *  onClose       () => void
 *  onConfirm     (targetStatusKey, orderStatusReasonKey?) => void
 *  title         string
 *  description   string
 *  isLoading     boolean
 */
const ReversalConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Reversal',
  description,
  isLoading = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [reasonKey, setReasonKey] = useState('');
  const [cancelReasons, setCancelReasons] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getOrderStatusReasons('cancelled')
      .then((res) => { if (!cancelled) setCancelReasons(res.reasons || []); })
      .catch(() => { if (!cancelled) setCancelReasons([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const isYesTyped = inputValue.trim() === 'YES';

  const handleConfirm = () => {
    if (!isYesTyped) return;
    onConfirm('confirmed');
  };

  const handleStartCancelFlow = () => {
    if (!isYesTyped) return;
    setShowCancelReason(true);
  };

  const handleConfirmCancel = () => {
    if (!reasonKey) return;
    onConfirm('cancelled', reasonKey);
  };

  const handleBack = () => {
    setShowCancelReason(false);
    setReasonKey('');
  };

  const handleClose = () => {
    setInputValue('');
    setShowCancelReason(false);
    setReasonKey('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.warningIcon}>
              <AlertTriangle size={24} />
            </div>
            <h3 className={styles.title}>{title}</h3>
          </div>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.body}>
          {description && (
            <p className={styles.description}>{description}</p>
          )}

          <div className={styles.warning}>
            <AlertTriangle size={16} />
            <span>
              This action <strong>cannot be undone</strong>. Airwaybill numbers will be
              cleared and stock movements will be reversed. Choose whether the affected
              order(s) should return to <strong>Confirmed</strong> status, or move to{' '}
              <strong>Cancelled</strong> status instead.
            </span>
          </div>

          <div className={styles.inputSection}>
            <label className={styles.inputLabel}>
              Type <strong>YES</strong> to confirm:
            </label>
            <input
              className={styles.input}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type YES"
              disabled={isLoading || showCancelReason}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleClose();
              }}
            />
          </div>

          {showCancelReason && (
            <div className={styles.inputSection}>
              <label className={styles.inputLabel}>
                Reason for cancellation: <strong>*</strong>
              </label>
              <select
                className={styles.reasonSelect}
                value={reasonKey}
                onChange={(e) => setReasonKey(e.target.value)}
                disabled={isLoading}
                required
                autoFocus
              >
                <option value="">Select a reason</option>
                {cancelReasons.map((reason) => (
                  <option key={reason.key} value={reason.key}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {!showCancelReason ? (
            <>
              <button
                className={styles.cancelButton}
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleStartCancelFlow}
                disabled={!isYesTyped || isLoading}
              >
                Reverse & Cancel
              </button>
              <button
                className={styles.confirmPrimaryButton}
                onClick={handleConfirm}
                disabled={!isYesTyped || isLoading}
              >
                {isLoading ? <span className={styles.spinner} /> : null}
                {isLoading ? 'Reversing...' : 'Reverse & Confirm'}
              </button>
            </>
          ) : (
            <>
              <button
                className={styles.cancelButton}
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleConfirmCancel}
                disabled={!reasonKey || isLoading}
              >
                {isLoading ? <span className={styles.spinner} /> : null}
                {isLoading ? 'Reversing...' : 'Confirm Reverse & Cancel'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReversalConfirmDialog;
